import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
//Helpers
import { dateFormat } from '../../date_helper';
import { getTemplateServiceByDeviceID } from '../template_helpers';
import { capFirstChar } from '../../utilities';
//Types
import type { TemplateService } from 'src/types/VirtualComponents';
import type { ChartDataInterface } from 'src/types/Chart';

export type SessionEvents = {
	session_start: Date;
	session_end: Date;
	charger_start: Date;
	charger_end: Date;
	charger_plugged: Date;
	charger_unplugged: Date;
	charger_complete: Date;
	statistics?: {
		chartData: ChartDataInterface;
		consumption: string | null;
	};
};

export type SessionInfo = {
	title: string;
	state: string;
	icon: string;
	color?: any;
};

export const getSessionsStatus = (deviceID: string, sessionEvents: SessionEvents): SessionInfo[] => {
	const $format = get(format);
	const templateService: TemplateService = getTemplateServiceByDeviceID(deviceID);
	const sessions: SessionInfo[] = [];
	if (sessionEvents) {
		sessions.push({
			title: $format('label_status', { default: 'Status' }),
			state: $format('label_completed', { default: 'Completed' }),
			color: 'text-color-electric-vehicle',
			icon: null,
		});
		sessions.push({
			title: $format('mode_auto_balance', { default: 'Auto balance' }),
			state: capFirstChar(
				templateService.settings?.auto_balance?.enable
					? $format('xt1_template_auto', { default: 'Auto' })
					: $format('xt1_template_manual', { default: 'Manual' }),
			),
			icon: null,
		});
	}
	return sessions;
};

export const formatDuration = (sessionEvents: SessionEvents): string => {
	const durationMs = sessionEvents.session_end.getTime() - sessionEvents.session_start.getTime();
	if (durationMs > 0) {
		const totalMinutes = Math.floor(durationMs / (1000 * 60));
		return getTimeDuration(totalMinutes);
	}
	return '';
};

export const getTimeDuration = (minutes: number) => {
	const $format = get(format);
	const hours = Math.floor(minutes / 60);
	const min = minutes % 60;
	return `${hours}${$format('label_h_countdow', { default: 'h' })} ${min.toString().padStart(2, '0')}${$format('timer_text_min', { default: 'timer_text_min' })}`;
};

export const getSessionsInfo = (
	sessionEvents: SessionEvents,
	sessionDateFormat = 'H:i',
): SessionInfo[] => {
	const $format = get(format);
	const sessions: SessionInfo[] = [];
	if (sessionEvents) {
		if (sessionEvents.statistics?.consumption !== null) {
			sessions.push({
				title: $format('label_energy_delivered', { default: 'Energy delivered' }),
				state: sessionEvents.statistics?.consumption,
				icon: 'fas fa-gauge-circle-bolt',
			});
		}
		const duration = formatDuration(sessionEvents);
		if (duration) {
			sessions.push({
				title: sessionEvents.charger_plugged
					? $format('label_session_duration', { default: 'Session duration' })
					: $format('label_charging_duration', { default: 'Charging duration' }),
				state: duration,
				icon: 'fas fa-clock',
			});
		}
		if (sessionEvents.charger_plugged) {
			sessions.push({
				title: $format('label_plugged_at', { default: 'Plugged at' }),
				state: dateFormat(sessionEvents.charger_plugged, sessionDateFormat),
				icon: 'fas fa-plug',
			});
		}
		if (sessionEvents.charger_start) {
			sessions.push({
				title: $format('label_charging_started_at', { default: 'Charging started at' }),
				state: dateFormat(sessionEvents.charger_start, sessionDateFormat),
				icon: 'fas fa-plug',
			});
		}
		if (sessionEvents.charger_complete) {
			sessions.push({
				title: $format('label_completed_at', { default: 'Completed at' }),
				state: dateFormat(sessionEvents.charger_complete, sessionDateFormat),
				icon: 'fas fa-check',
			});
		}
		if (sessionEvents.charger_end) {
			sessions.push({
				title: $format('label_charging_ended_at', { default: 'Charging ended at' }),
				state: dateFormat(sessionEvents.charger_end, sessionDateFormat),
				icon: 'fas fa-plug-circle-xmark',
			});
		}
		if (sessionEvents.charger_unplugged) {
			sessions.push({
				title: $format('label_unplugged_at', { default: 'Unplugged at' }),
				state: dateFormat(sessionEvents.session_end, sessionDateFormat),
				icon: 'fas fa-plug-circle-xmark',
			});
		}
	}
	return sessions;
};
